import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Col,
  Row,
  Checkbox,
  Typography,
  Card,
  Spin,
  Flex,
} from "antd";
import { useUsersServiceGetRole, useUsersServiceUpdateRole } from "api/queries";
import { useParams } from "react-router-dom";
import { SubjectKey, PermissionType, ExtraPermissionType } from "utils/enums";

const { Title } = Typography;

const EditRole: React.FC = () => {
  const [form] = Form.useForm();
  const { id } = useParams<{ id: string }>();

  // Default CRUD actions
  const defaultActions = [
    PermissionType.CREATE,
    PermissionType.READ,
    PermissionType.UPDATE,
    PermissionType.DELETE,
  ];

  // Per-subject overrides: add any extra permissions here
  const subjectActionMap: Partial<Record<SubjectKey, any[]>> = {
    // add more subject-specific overrides as needed
    // [SubjectKey.REPORTS]: [
    //   PermissionType.CREATE,
    //   PermissionType.READ,
    //   PermissionType.UPDATE,
    //   PermissionType.DELETE,
    //   PermissionType.DOWNLOAD,
    //   PermissionType.PRINT,
    // ],
    // [SubjectKey.SETTINGS]: [
    //   PermissionType.READ,
    //   PermissionType.UPDATE,
    //   PermissionType.PRINT,
    // ],
    [SubjectKey.INVOICE]: [
      ExtraPermissionType.READ_KPIS,
      ExtraPermissionType.MANAGE,
      PermissionType.READ,
      PermissionType.UPDATE,
      PermissionType.DELETE,
      PermissionType.CREATE,
    ],
    [SubjectKey.REPARATIONS]: [
      ExtraPermissionType.EDIT_PRICING,
      ExtraPermissionType.DELETE_PRICING,
      ExtraPermissionType.MANAGE,
      PermissionType.READ,
      PermissionType.UPDATE,
      PermissionType.DELETE,
      PermissionType.CREATE,
    ],
    [SubjectKey.INVENTORY_STOCK]: [
      ExtraPermissionType.READ_KPIS,
      PermissionType.READ,
      PermissionType.UPDATE,
      PermissionType.DELETE,
      PermissionType.CREATE,
    ],
    [SubjectKey.PRODUCTS]: [
      ExtraPermissionType.READ_PRODUCT_COST,
      PermissionType.READ,
      PermissionType.CREATE,
      PermissionType.UPDATE,
      PermissionType.DELETE,
      PermissionType.DOWNLOAD,
    ],
  };

  const allSubjectActions: Record<SubjectKey, PermissionType[]> = Object.values(
    SubjectKey
  ).reduce((acc, subject) => {
    acc[subject] = subjectActionMap[subject] || defaultActions;
    return acc;
  }, {} as Record<SubjectKey, PermissionType[]>);

  const initialPermissions: Record<
    string,
    Record<string, boolean>
  > = Object.entries(allSubjectActions).reduce((acc, [subject, actions]) => {
    acc[subject] = actions.reduce((map, action) => {
      map[action] = false;
      return map;
    }, {} as Record<string, boolean>);
    return acc;
  }, {} as Record<string, Record<string, boolean>>);

  const [checkedPermissions, setCheckedPermissions] =
    useState(initialPermissions);
  const [indeterminateStates, setIndeterminateStates] = useState<
    Record<string, boolean>
  >({});

  const { data, isLoading, isRefetching, refetch, isError } =
    useUsersServiceGetRole({ roleId: Number(id) });

  const { mutate: updateRole, isPending } = useUsersServiceUpdateRole({
    onError: (err) => console.dir(err),
    onSuccess: async () => {
      await refetch();
    },
  });

  // Merge backend permissions with defaults
  useEffect(() => {
    if (!data) return;

    const merged = Object.entries(allSubjectActions).reduce(
      (acc, [subject, actions]) => {
        //@ts-ignore
        const existing = data.rolePermissions?.[subject] || {};
        acc[subject] = actions.reduce((map, action) => {
          map[action] = existing[action] ?? false;
          return map;
        }, {} as Record<string, boolean>);
        return acc;
      },
      {} as Record<string, Record<string, boolean>>
    );

    setCheckedPermissions(merged);
    form.setFieldsValue({
      roleName: data.roleName,
      roleDescription: data.roleDescription,
    });
  }, [data, form]);

  // Compute indeterminate states per subject
  useEffect(() => {
    const newStates: Record<string, boolean> = {};
    Object.entries(allSubjectActions).forEach(([subject, actions]) => {
      const values = actions.map((a) => checkedPermissions[subject][a]);
      newStates[subject] = !values.every(Boolean) && !values.every((v) => !v);
    });
    setIndeterminateStates(newStates);
  }, [checkedPermissions]);

  // Handlers
  const handleCheckboxChange = (
    subject: SubjectKey,
    action: PermissionType,
    checked: boolean
  ) => {
    setCheckedPermissions((prev) => ({
      ...prev,
      [subject]: {
        ...prev[subject],
        [action]: checked,
      },
    }));
  };

  const handleGroupCheckboxChange = (subject: SubjectKey, checked: boolean) => {
    setCheckedPermissions((prev) => ({
      ...prev,
      [subject]: allSubjectActions[subject].reduce((map, action) => {
        map[action] = checked;
        return map;
      }, {} as Record<string, boolean>),
    }));
  };

  const onFinish = () => {
    updateRole({
      roleId: Number(id),
      requestBody: {
        roleName: form.getFieldValue("roleName"),
        roleDescription: form.getFieldValue("roleDescription"),
        //@ts-ignore
        rolePermissions: checkedPermissions,
      },
    });
  };

  return (
    <Row gutter={16}>
      {(isLoading || isRefetching || isPending) && (
        <Col span={24}>
          <Flex justify="center" align="center" style={{ minHeight: 500 }}>
            <Spin />
          </Flex>
        </Col>
      )}

      {!isError && !(isLoading || isRefetching) && (
        <>
          <Col
            xs={24}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Title level={2} style={{ margin: 0 }}>
              Edit Role
            </Title>
            <Button type="primary" onClick={() => form.submit()}>
              Save
            </Button>
          </Col>

          <Col span={24}>
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              initialValues={{
                roleName: data?.roleName,
                roleDescription: data?.roleDescription,
              }}
            >
              <Form.Item
                label="Role Name"
                name="roleName"
                rules={[{ required: true }]}
              >
                <Input placeholder="Enter role name" size="large" />
              </Form.Item>

              <Form.Item label="Role Description" name="roleDescription">
                <Input.TextArea placeholder="Enter role description" rows={4} />
              </Form.Item>

              <Title level={3} style={{ marginBottom: 16 }}>
                Permissions
              </Title>

              {Object.values(SubjectKey).map((subject) => {
                const actions = allSubjectActions[subject];
                return (
                  <Card
                    key={subject}
                    title={
                      <Checkbox
                        indeterminate={indeterminateStates[subject]}
                        checked={actions.every(
                          (a) => checkedPermissions[subject][a]
                        )}
                        onChange={(e) =>
                          handleGroupCheckboxChange(subject, e.target.checked)
                        }
                      >
                        {subject.replace(/_/g, " ")}
                      </Checkbox>
                    }
                    bordered={false}
                    style={{ marginBottom: 16 }}
                  >
                    <Row gutter={16}>
                      {actions.map((action) => (
                        <Col span={6} key={action}>
                          <Checkbox
                            checked={checkedPermissions[subject][action]}
                            onChange={(e) =>
                              handleCheckboxChange(
                                subject,
                                action,
                                e.target.checked
                              )
                            }
                          >
                            {action
                              .toLowerCase()
                              .replace(/^\w/, (c) => c.toUpperCase())}
                          </Checkbox>
                        </Col>
                      ))}
                    </Row>
                  </Card>
                );
              })}
            </Form>
          </Col>
        </>
      )}
    </Row>
  );
};

export default EditRole;
